import { useState } from 'react';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Chip, Grid, Button } from '@mui/material';
import { GetRequestHistory } from '../../../../api/MyIMO';
import RequestDetailsModal from '../modal/RequestDetailsModal';
import { Snackbar } from '../../../../components/Global/Snackbar/Snackbar';
import { toTitleCase } from '../../../../components/Utilities/HelperMethods';

const ChangeRequestDataGrid = ({ data, isLoading }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetailsModalOpen, setRequestDetailsModalOpen] = useState(false);

  const getRequestHistory = useQuery({
    queryKey: ['getRequestHistory', selectedRequest?.id],
    queryFn: GetRequestHistory(selectedRequest?.id),
    enabled: !!selectedRequest?.id,
  });

  const toggleRequestDetailsModal = () => {
    setRequestDetailsModalOpen((prev) => !prev);
  };

  const handleOpenRequestModal = (request) => {
    setSelectedRequest(request);
    toggleRequestDetailsModal();
  };

  const columns = [
    {
      field: 'producerID',
      headerName: 'Producer',
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      flex: 2,
      renderCell: (params) => {
        const { dataObject, producerID } = params.formattedValue;
        const { FirstName, LastName } = dataObject ? JSON.parse(dataObject) : {};
        const chipLabel =
          producerID !== null
            ? toTitleCase(`${FirstName} ${LastName}`) + ` - ${producerID}`
            : toTitleCase(`${FirstName} ${LastName}`);
        return (
          <Chip
            variant="outlined"
            label={FirstName !== undefined && LastName !== undefined ? chipLabel : '----'}
          />
        );
      },
    },
    {
      field: 'requestTypeName',
      headerName: 'Request Type',
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'dateSubmitted',
      type: 'date',
      headerName: 'Request Date',
      headerAlign: 'left',
      align: 'left',
      minWidth: 120,
      flex: 1,
      valueGetter: (value) => value && new Date(value),
    },
    {
      field: 'statusName',
      headerName: 'Status',
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      flex: 2,
      renderCell: (params) => {
        return (
          <Chip
            variant="outlined"
            label={params.formattedValue}
            color={
              params.formattedValue.includes('Complete')
                ? 'success'
                : params.formattedValue.includes('Rejected')
                  ? 'error'
                  : 'default'
            }
          />
        );
      },
    },
    {
      field: 'statusDate',
      type: 'date',
      headerName: 'Status Date',
      headerAlign: 'left',
      align: 'left',
      minWidth: 120,
      flex: 1,
      valueGetter: (value) => value && new Date(value),
    },
    {
      field: 'details',
      headerName: 'Details',
      type: 'actions',
      headerAlign: 'left',
      align: 'left',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const details = params.formattedValue ? JSON.parse(params.formattedValue) : {};
        details.id = params.row.id ? params.row.id : 0;
        return (
          <Button variant="outlined" onClick={() => handleOpenRequestModal(details)}>
            View Details
          </Button>
        );
      },
    },
  ];

  const rows = data?.map((request) => ({
    id: request.requestID,
    producerID: request,
    requestTypeName: request.requestDisplayName,
    dateSubmitted: request.dateSubmitted,
    statusName: request.statusName,
    statusDate: request.statusDate,
    details: request.dataObject,
  }));

  return (
    <Grid sx={{ width: '100%' }}>
      {data && (
        <>
          <DataGrid
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiDataGrid-row.Mui-hovered': {
                backgroundColor: 'transparent',
              },
              '& p': {
                margin: 0,
              },
              '& .MuiTablePagination-actions': {
                marginTop: 0,
              },
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: 'transparent',
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
              height: 600,
            }}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            disableColumnSelector
            loading={isLoading}
            slotProps={{
              loadingOverlay: {
                variant: 'skeleton',
              },
            }}
          />
          {selectedRequest && getRequestHistory.isSuccess && getRequestHistory.data && (
            <RequestDetailsModal
              requestDetailsModalOpen={requestDetailsModalOpen}
              toggleRequestDetailsModal={toggleRequestDetailsModal}
              selectedRequest={selectedRequest}
              requestHistory={getRequestHistory.data}
            />
          )}
          <Snackbar
            open={getRequestHistory.isError}
            message={"Couldn't retrieve request details"}
            status="error"
          />
        </>
      )}
    </Grid>
  );
};

export default ChangeRequestDataGrid;
